import { useEffect, useState, useCallback } from 'react';
import { useEmbedded } from '@/store';

const listeners = new Set();

const handleMessage = (event, source) => {
  listeners.forEach((callback) => {
    try {
      callback(event?.data);
    } catch (e) {
      console.error('Error in postMessage subscriber:', e);
    }
  });
};

if (typeof window !== 'undefined') {
  document.addEventListener('message', (e) => handleMessage(e, 'document'));
  window.addEventListener('message', (e) => handleMessage(e, 'window'));
}

/**
 * usePostMessages is a custom hook that provides a function to send post messages to the parent window.
 * It checks if the app is embedded in a WebView and sends the message using the ReactNativeWebView.postMessage method.
 * If the app is not embedded, it does nothing.
 *
 * @param {Function} onMessage - Optional callback function to handle received post messages.
 * @returns {Object} An object containing the sendPostMessage function.
 */

export default function usePostMessages(onMessage) {
  const { isEmbedded } = useEmbedded();
  /**
   * Verify if postMessage property exists in DOM
   * @param {number} retries - Max retry number
   * @param {number} delay - ms delay
   * @returns {Promise<boolean>} - Promise true if property exists
   */

  const checkPostMessageAvailability = useCallback(
    async (retries = 40, delay = 100) => {
      for (let attempt = 0; attempt < retries; attempt++) {
        if (window?.ReactNativeWebView?.postMessage) {
          return true;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      return false;
    },
    [],
  );

  /**
   * Send post Message with custom  message
   * @param {string} message - Message to send
   * @returns void
   *
   * @example
   * sendPostMessage('Hello, world!');
   *
   */
  const sendPostMessage = async (message) => {
    if (!isEmbedded) return;

    try {
      const isPostMessageAvailable = await checkPostMessageAvailability(40);

      if (!isPostMessageAvailable) {
        return;
      }

      const params = [message];

      if (!window.navigator.userAgent.includes('Android')) params.push('*');

      window.ReactNativeWebView.postMessage(...params);
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Send post Message to disable back button
   * @param {boolean} enable - Decide if send enableBackButton or disableBackButton message
   * @returns void
   *
   * @example
   * backButtonMessage(true); // sends enableBackButton message
   * backButtonMessage(false); // sends disableBackButton message
   *
   */

  const backButtonMessage = async (enable) => {
    if (!isEmbedded) return;
    await sendPostMessage(enable ? 'enableBackButton' : 'disableBackButton');
  };

  const closeCheckoutMessage = async (data) => {
    if (!isEmbedded) return;
    await sendPostMessage(
      JSON.stringify({
        message: 'checkoutData',
        checkoutData: data,
      }),
    );
  };

  /**
   * Register a callback to receive post messages.
   * @param {Function} callback - Function to be called when a message is received.
   * @returns {Function} - Unsubscribe function.
   */
  const onReceiveMessage = useCallback(
    (callback = () => {}) => {
      if (!isEmbedded || !callback) return () => {};
      listeners.add(callback);
      return () => listeners.delete(callback);
    },
    [isEmbedded],
  );

  useEffect(() => {
    if (onMessage && isEmbedded) {
      return onReceiveMessage(onMessage);
    }
  }, [isEmbedded, onMessage, onReceiveMessage]);

  return {
    sendPostMessage,
    backButtonMessage,
    closeCheckoutMessage,
    onReceiveMessage,
    isEmbedded,
  };
}
